import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect, useState } from 'react';
import NotificationSound from '../assets/notification_1.mp3';
import PopSound from '../assets/sounds/pop.wav';

const NotificationContext = createContext();

// Tipos de notificação
export const NOTIFICATION_TYPES = {
  NEW_MESSAGE: 'NEW_MESSAGE',
  NEW_CLIENT: 'NEW_CLIENT',
  TRANSFER: 'TRANSFER',
  SYSTEM: 'SYSTEM',
  ANNOUNCEMENT: 'ANNOUNCEMENT',
  COINS_EARNED: 'COINS_EARNED',
  CELEBRATION: 'CELEBRATION',
  FEEDBACK: 'FEEDBACK',
  SCHEDULED_MEETING: 'SCHEDULED_MEETING',
  COMMENT: 'COMMENT'
};

// Reducer para gerenciar notificações locais
const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, {
          id: Date.now(),
          type: action.payload.type,
          title: action.payload.title,
          message: action.payload.message,
          roomId: action.payload.roomId,
          timestamp: new Date(),
          read: false
        }]
      };
    
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(notification =>
          notification.id === action.payload ? { ...notification, read: true } : notification
        )
      };
    
    case 'CLEAR_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(notification => notification.id !== action.payload)
      };
    
    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: []
      };
    
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: []
  });
  
  const [hasInteracted, setHasInteracted] = useState(false);
  const notificationAudio = useRef(new Audio(NotificationSound));
  const popNotificationAudio = useRef(new Audio(PopSound));

  // Verificar interação do usuário para tocar sons
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasInteracted(true);
      window.removeEventListener('click', handleUserInteraction);
    };

    window.addEventListener('click', handleUserInteraction);
    return () => window.removeEventListener('click', handleUserInteraction);
  }, []);

  // Pré-carregar áudios após interação
  useEffect(() => {
    if (hasInteracted) {
      notificationAudio.current.load();
      popNotificationAudio.current.load();
    }
  }, [hasInteracted]);

  // Função para mostrar notificações
  const showNotification = useCallback((title, message, soundType = 'notification') => {
    try {
      if (!("Notification" in window)) {
        console.warn("Este navegador não suporta notificações de desktop.");
        return;
      }

      const soundFile = soundType === 'pop' ? popNotificationAudio.current : notificationAudio.current;

      if (Notification.permission === "granted") {
        new Notification(title, { 
          body: message, 
          icon: '/logo192.png'
        });
        
        if (hasInteracted) {
          soundFile.play().catch(error => console.warn("Erro ao tocar som:", error));
        }
      } else if (Notification.permission !== "denied") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            new Notification(title, { 
              body: message, 
              icon: '/logo192.png'
            });
            
            if (hasInteracted) {
              soundFile.play().catch(error => console.warn("Erro ao tocar som:", error));
            }
          }
        });
      }
    } catch (error) {
      console.error("Erro ao mostrar notificação:", error);
    }
  }, [hasInteracted]);

  // Função para adicionar notificação
  const addNotification = useCallback((type, title, message, roomId = null) => {
    dispatch({
      type: 'ADD_NOTIFICATION',
      payload: { type, title, message, roomId }
    });

    // Mostrar notificação de desktop
    const soundType = type === NOTIFICATION_TYPES.NEW_CLIENT || type === NOTIFICATION_TYPES.TRANSFER ? 'pop' : 'notification';
    showNotification(title, message, soundType);
  }, [showNotification]);

  // Função para marcar como lida
  const markAsRead = useCallback((notificationId) => {
    dispatch({
      type: 'MARK_AS_READ',
      payload: notificationId
    });
  }, []);

  // Função para limpar notificação
  const clearNotification = useCallback((notificationId) => {
    dispatch({
      type: 'CLEAR_NOTIFICATION',
      payload: notificationId
    });
  }, []);

  // Função para limpar todas
  const clearAllNotifications = useCallback(() => {
    dispatch({
      type: 'CLEAR_ALL'
    });
  }, []);

  // Função para marcar todas como lidas
  const markAllAsRead = useCallback(() => {
    state.notifications.forEach(notification => {
      if (!notification.read) {
        dispatch({
          type: 'MARK_AS_READ',
          payload: notification.id
        });
      }
    });
  }, [state.notifications, dispatch]);

  // Funções específicas para cada tipo de notificação
  const notifyNewMessage = useCallback((roomId, message) => {
    addNotification(NOTIFICATION_TYPES.NEW_MESSAGE, 'Cliente', message, roomId);
  }, [addNotification]);

  const notifyNewClient = useCallback((clientName) => {
    addNotification(NOTIFICATION_TYPES.NEW_CLIENT, 'Novo cliente em espera', clientName);
  }, [addNotification]);

  const notifyTransfer = useCallback((fromName) => {
    addNotification(NOTIFICATION_TYPES.TRANSFER, 'Conversa transferida', `Transferida de ${fromName}`);
  }, [addNotification]);

  const notifySystem = useCallback((title, message) => {
    addNotification(NOTIFICATION_TYPES.SYSTEM, title, message);
  }, [addNotification]);

  const value = {
    // Estado
    notifications: state.notifications,
    unreadCount: state.notifications.filter(n => !n.read).length,
    
    // Funções do contexto
    addNotification,
    markAsRead,
    clearNotification,
    clearAllNotifications,
    markAllAsRead,
    showNotification,
    
    // Funções específicas
    notifyNewMessage,
    notifyNewClient,
    notifyTransfer,
    notifySystem
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Hook para usar o contexto
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext deve ser usado dentro de um NotificationProvider');
  }
  return context;
};
